import { useState, useEffect, useCallback } from 'react';

export type ResolutionOptionId = 'reassign' | 'split' | 'reduce' | 'delay';

export interface ResolutionOptionConfig {
  id:          ResolutionOptionId;
  label:       string;
  description: string;
  /** One-line note shown in Settings explaining when this is relevant */
  guidance:    string;
  /** Off by default for professional services (e.g. split booking) */
  defaultOn:   boolean;
}

export const RESOLUTION_OPTION_CONFIGS: ResolutionOptionConfig[] = [
  {
    id:          'reassign',
    label:       'Reassign to team member',
    description: 'Replace the overloaded resource with an available internal team member',
    guidance:    'Moves the booking to a free colleague with matching skills. No timeline impact.',
    defaultOn:   true,
  },
  {
    id:          'reduce',
    label:       'Reduce booking hours',
    description: 'Lower the weekly commitment to bring utilisation back under 100%',
    guidance:    'Useful when scope can be negotiated with the project owner or the engagement can be stretched over a longer period.',
    defaultOn:   true,
  },
  {
    id:          'delay',
    label:       'Delay the booking',
    description: 'Keep the original resource but push the start date to when they next have capacity',
    guidance:    'Best when the client can flex the start date and no downstream bookings are critically dependent on the original schedule.',
    defaultOn:   true,
  },
  {
    id:          'split',
    label:       'Split the booking',
    description: 'Divide hours between the original resource and a second person',
    guidance:    'Suitable when two people can share responsibility for a booking. Works best when the work can be clearly divided.',
    defaultOn:   false,
  },
];

const STORAGE_KEY        = 'conflict-resolution-enabled-options';
const WHAT_IF_KEY        = 'conflict-resolution-what-if-enabled';

function getDefaultEnabled(): Set<ResolutionOptionId> {
  return new Set(
    RESOLUTION_OPTION_CONFIGS.filter((c) => c.defaultOn).map((c) => c.id),
  );
}

function loadFromStorage(): Set<ResolutionOptionId> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ResolutionOptionId[];
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    return new Set(parsed);
  } catch {
    return null;
  }
}

function saveToStorage(enabled: Set<ResolutionOptionId>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...enabled]));
  } catch {
    // ignore
  }
}

function loadWhatIfEnabled(): boolean {
  try {
    const raw = localStorage.getItem(WHAT_IF_KEY);
    if (raw === null) return true; // on by default
    return raw === 'true';
  } catch {
    return true;
  }
}

function saveWhatIfEnabled(val: boolean): void {
  try {
    localStorage.setItem(WHAT_IF_KEY, String(val));
  } catch {
    // ignore
  }
}

export function useConflictResolutionSettings() {
  const [enabled, setEnabled] = useState<Set<ResolutionOptionId>>(
    () => loadFromStorage() ?? getDefaultEnabled(),
  );

  const [whatIfEnabled, setWhatIfEnabled] = useState<boolean>(
    () => loadWhatIfEnabled(),
  );

  // Keep storage in sync whenever state changes
  useEffect(() => { saveToStorage(enabled); }, [enabled]);
  useEffect(() => { saveWhatIfEnabled(whatIfEnabled); }, [whatIfEnabled]);

  const toggle = useCallback((id: ResolutionOptionId) => {
    setEnabled((prev) => {
      // Always keep at least one option enabled
      if (prev.has(id) && prev.size === 1) return prev;
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const isEnabled = useCallback(
    (id: ResolutionOptionId) => enabled.has(id),
    [enabled],
  );

  const toggleWhatIf = useCallback(() => {
    setWhatIfEnabled((prev) => !prev);
  }, []);

  return { enabled, toggle, isEnabled, whatIfEnabled, toggleWhatIf };
}
